import { useState, useEffect } from 'react';
import { OnboardingStepHeader } from './OnboardingStepHeader';
import { OnboardingFooter } from './OnboardingFooter';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/lib/organization-context';
import { successToast, errorToast } from '@/components/ui/sonner';
import { INDUSTRY_OPTIONS as CENTRAL_INDUSTRY_OPTIONS } from '@/lib/projectConstants';

interface OrganizationSetupProps {
  onComplete: () => void;
  onSkip: () => void;
  onBack?: () => void;
}

const INDUSTRY_OPTIONS = CENTRAL_INDUSTRY_OPTIONS.map(opt => opt.label);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  background: 'rgba(255,255,255,0.04)',
  color: '#fff',
  fontSize: 13,
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(255,255,255,0.6)',
  marginBottom: 6,
  display: 'block',
};

export function OrganizationSetup({ onComplete }: Omit<OrganizationSetupProps, 'onSkip' | 'onBack'>) {
  const { organization, refetch } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    website: '',
    industry_focus: [] as string[],
  });
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (!organization) return;
    setFormData(prev => ({
      name: organization.name || prev.name,
      tagline: organization.tagline || prev.tagline,
      website: organization.website || prev.website,
      industry_focus: organization.industry_focus?.length ? organization.industry_focus : prev.industry_focus,
    }));
  }, [organization]);

  const validateForm = () => {
    const newErrors: { name?: string } = {};
    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = formData.name.trim().length > 0;

  const handleIndustryToggle = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industry_focus: prev.industry_focus.includes(industry)
        ? prev.industry_focus.filter(i => i !== industry)
        : [...prev.industry_focus, industry],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization?.id) return;
    if (!validateForm()) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: formData.name.trim() || organization.name,
          tagline: formData.tagline.trim() || null,
          website: formData.website.trim() || null,
          industry_focus: formData.industry_focus.length > 0 ? formData.industry_focus : null,
        })
        .eq('id', organization.id);
      if (error) throw error;
      await refetch();
      successToast('Organization updated', 'Your organization details have been saved.');
      onComplete();
    } catch (error) {
      console.error('Error updating organization:', error);
      errorToast('Error', 'Failed to update organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const focusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'rgba(194,84,26,0.5)';
    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
  };
  const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
  };

  return (
    <>
      <OnboardingStepHeader
        step={2}
        totalSteps={5}
        title="Tell us about your organisation."
        subtitle="This appears on your proposal cover pages and helps the AI understand your business context."
      />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>
              Company Name <span style={{ color: '#C2541A' }}>*</span>
            </label>
            <input
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              placeholder="Your company name"
              style={inputStyle}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
            {errors.name && (
              <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{errors.name}</p>
            )}
          </div>

          <div>
            <label style={labelStyle}>
              Tagline <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              value={formData.tagline}
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
              placeholder="e.g., Digital Transformation Experts"
              style={inputStyle}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </div>

          <div>
            <label style={labelStyle}>
              Website <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://yourcompany.com"
              style={inputStyle}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
          </div>

          <div>
            <label style={labelStyle}>
              Industry Focus <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(select all that apply)</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {INDUSTRY_OPTIONS.map((industry) => {
                const isSelected = formData.industry_focus.includes(industry);
                return (
                  <button
                    key={industry}
                    type="button"
                    onClick={() => handleIndustryToggle(industry)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 999,
                      fontSize: 12,
                      border: `1px solid ${isSelected ? 'rgba(194,84,26,0.4)' : 'rgba(255,255,255,0.1)'}`,
                      color: isSelected ? '#C2541A' : 'rgba(255,255,255,0.5)',
                      background: isSelected ? 'rgba(194,84,26,0.15)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 150ms',
                    }}
                  >
                    {industry}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <OnboardingFooter loading={loading} disabled={!isFormValid} />
      </form>
    </>
  );
}
